import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import NotificationMessagingSection from '../../components/NotificationMessagingSection';
import { HomeNavButton } from '../../components/NavButtons';
import {
  filterEventNotifications,
  filterMessageNotifications,
  filterSentNotificationsBySender,
  filterStudentNotifications,
  getEventNotificationLabel,
  getNotificationSenderTitle,
  getReceivedNotifications,
  getSentNotifications,
} from '../../services/notificationService';
import { focusTheme } from '../../utils/focusTheme';

const unreadCardTheme = focusTheme.status.active;

const tabs = [
  { id: 'inbox', label: 'پیام‌ها' },
  { id: 'alerts', label: 'اطلاعیه‌ها' },
  { id: 'compose', label: 'ارسال پیام' },
  { id: 'sent', label: 'ارسال‌شده' },
];

const tabCopy = {
  inbox: {
    title: 'لیست پیام‌ها',
    description: 'پیام‌های معلم و مدرسه را انتخاب کن و بخوان.',
  },
  alerts: {
    title: 'لیست اطلاعیه‌ها',
    description: 'اعلان‌های سیستم مثل تصحیح تکلیف را جدا از پیام‌ها اینجا ببین.',
  },
  compose: {
    title: 'ارسال پیام',
    description: 'برای معلم یا مدرسه پیام بفرست.',
  },
  sent: {
    title: 'پیام‌های ارسال‌شده',
    description: 'پیام‌هایی که قبلاً فرستادی را اینجا ببین.',
  },
};

function formatNotificationDate(value) {
  if (!value) {
    return 'بدون تاریخ';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'بدون تاریخ';
  }

  return new Intl.DateTimeFormat('fa-IR', {
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    month: 'long',
  }).format(date);
}

function getSenderLabel(notification) {
  return getNotificationSenderTitle(notification);
}

function NotificationListCard({ notification, showEventLabel = false }) {
  const isUnread = !notification.isSeen;
  const eventLabel = showEventLabel ? getEventNotificationLabel(notification) : '';

  return (
    <Link
      to={`/notifications/${notification.id}`}
      className={`block rounded-[5px] border px-3 py-3 transition active:scale-[0.99] ${
        isUnread
          ? `${unreadCardTheme.borderClass} text-slate-800`
          : 'border-slate-200 bg-white text-slate-500'
      }`}
      style={{
        background: isUnread ? unreadCardTheme.background : '#FFFFFF',
      }}
    >
      <div className="flex items-start gap-3">
        <span
          className={`mt-2 h-2 w-2 shrink-0 rounded-full ${
            isUnread ? 'bg-teal-500' : 'bg-transparent'
          }`}
          aria-hidden={!isUnread}
          aria-label={isUnread ? 'خوانده‌نشده' : undefined}
        />

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <p
              className={`truncate text-[14px] leading-6 ${
                isUnread ? 'font-semibold text-slate-900' : 'font-medium text-slate-500'
              }`}
            >
              {notification.title || 'بدون عنوان'}
            </p>

            <div className="flex shrink-0 items-center gap-1.5">
              {eventLabel ? (
                <span className="rounded-[4px] bg-white/85 px-2 py-0.5 text-[10px] font-semibold text-teal-700">
                  {eventLabel}
                </span>
              ) : null}
              {isUnread && (
                <span className={`rounded-[4px] px-2 py-0.5 text-[10px] font-semibold ${unreadCardTheme.badgeClass}`}>
                  جدید
                </span>
              )}
            </div>
          </div>

          <p
            className={`mt-1.5 line-clamp-2 text-[13px] leading-6 ${
              isUnread ? 'text-slate-700' : 'text-slate-400'
            }`}
          >
            {notification.description || 'متنی برای این اطلاعیه ثبت نشده است.'}
          </p>

          <div
            className={`mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] ${
              isUnread ? 'text-slate-500' : 'text-slate-400'
            }`}
          >
            <span>{getSenderLabel(notification)}</span>
            <span aria-hidden="true">·</span>
            <span>{formatNotificationDate(notification.createdAt)}</span>
            {notification.parentId ? (
              <>
                <span aria-hidden="true">·</span>
                <span>پاسخ</span>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </Link>
  );
}

function NotificationsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const activeTab = tabs.some((tab) => tab.id === tabFromUrl) ? tabFromUrl : 'inbox';
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [sentCount, setSentCount] = useState(0);

  const messageNotifications = useMemo(
    () => filterMessageNotifications(notifications),
    [notifications]
  );
  const eventNotifications = useMemo(
    () => filterEventNotifications(notifications),
    [notifications]
  );
  const unreadMessageCount = useMemo(
    () => messageNotifications.filter((notification) => !notification.isSeen).length,
    [messageNotifications]
  );
  const unreadEventCount = useMemo(
    () => eventNotifications.filter((notification) => !notification.isSeen).length,
    [eventNotifications]
  );

  function setActiveTab(tabId) {
    const nextParams = new URLSearchParams(searchParams);

    if (tabId === 'inbox') {
      nextParams.delete('tab');
    } else {
      nextParams.set('tab', tabId);
    }

    setSearchParams(nextParams, { replace: true });
  }

  useEffect(() => {
    let isMounted = true;

    async function loadNotifications() {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const receivedNotifications = await getReceivedNotifications();

        if (isMounted) {
          setNotifications(filterStudentNotifications(receivedNotifications));
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(error.message || 'امکان دریافت اطلاعیه‌ها وجود ندارد.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    async function loadSentCount() {
      try {
        const sentNotifications = await getSentNotifications();

        if (isMounted) {
          setSentCount(filterSentNotificationsBySender(sentNotifications, 'student').length);
        }
      } catch {
        // Badge stays optional if sent list fails to load.
      }
    }

    loadNotifications();
    loadSentCount();

    return () => {
      isMounted = false;
    };
  }, []);

  const currentCopy = tabCopy[activeTab] || tabCopy.inbox;
  const visibleItems = activeTab === 'alerts' ? eventNotifications : messageNotifications;
  const visibleUnreadCount = activeTab === 'alerts' ? unreadEventCount : unreadMessageCount;
  const itemNoun = activeTab === 'alerts' ? 'اطلاعیه' : 'پیام';

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <p
            style={{ background: focusTheme.header.background, boxShadow: focusTheme.header.boxShadow }}
            className="px-6 py-1 rounded-[10px] text-[15px] text-white"
          >
            اطلاعیه‌ها
          </p>
        </div>

        <HomeNavButton />
      </div>

      <div>
        <p className="text-[14px] font-semibold text-slate-900">
          {currentCopy.title}
        </p>
        <p className="mt-1 text-[13px] font-medium leading-6 text-slate-800">
          {currentCopy.description}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-1 rounded-[12px] bg-slate-100 p-1">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTab;
          const badgeCount =
            tab.id === 'inbox'
              ? unreadMessageCount
              : tab.id === 'alerts'
                ? unreadEventCount
                : tab.id === 'sent'
                  ? sentCount
                  : 0;

          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center justify-center gap-1.5 rounded-[9px] px-2 py-2.5 text-[12px] transition ${
                isActive
                  ? 'bg-white font-medium text-slate-800 shadow-sm'
                  : 'font-normal text-slate-500'
              }`}
            >
              <span className="truncate">{tab.label}</span>
              {badgeCount > 0 && (
                <span
                  className={`inline-flex min-w-[16px] items-center justify-center rounded-full px-1 text-[9px] tabular-nums ${
                    isActive ? 'bg-[#00C0B9]/12 text-[#00A8A2]' : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {badgeCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {(activeTab === 'inbox' || activeTab === 'alerts') && (
        <section className="space-y-2">
          {!isLoading && !errorMessage && visibleItems.length > 0 && (
            <p className="px-0.5 text-[12px] text-slate-500">
              {visibleUnreadCount > 0
                ? `${visibleUnreadCount} ${itemNoun} خوانده‌نشده از ${visibleItems.length} ${itemNoun}`
                : `${visibleItems.length} ${itemNoun}`}
            </p>
          )}

          <div
            className="flex flex-col gap-2 rounded-[8px] p-2"
            style={{ background: focusTheme.sectionBg }}
          >
            {isLoading ? (
              <div className="rounded-[8px] bg-white px-4 py-8 text-center text-[12px] text-slate-500">
                {activeTab === 'alerts' ? 'در حال دریافت اطلاعیه‌ها...' : 'در حال دریافت پیام‌ها...'}
              </div>
            ) : errorMessage ? (
              <div className="rounded-[8px] bg-white px-4 py-6 text-center">
                <p className={`text-[13px] font-bold ${focusTheme.message.error}`}>{errorMessage}</p>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className={`mt-3 rounded-[6px] px-5 py-1 text-[12px] font-bold ${focusTheme.button.retry}`}
                >
                  تلاش دوباره
                </button>
              </div>
            ) : visibleItems.length === 0 ? (
              <div className="rounded-[8px] bg-white px-4 py-8 text-center">
                <p className="text-[14px] font-medium text-slate-800">
                  {activeTab === 'alerts' ? 'فعلاً اطلاعیه‌ای نداری' : 'فعلاً پیامی نداری'}
                </p>
                <p className="mt-2 text-[12px] text-slate-500">
                  {activeTab === 'alerts'
                    ? 'وقتی تکلیف یا آزمونت بررسی شود، اینجا نمایش داده می‌شود.'
                    : 'وقتی پیام جدیدی ارسال شود، اینجا نمایش داده می‌شود.'}
                </p>
              </div>
            ) : (
              visibleItems.map((notification) => (
                <NotificationListCard
                  key={notification.id}
                  notification={notification}
                  showEventLabel={activeTab === 'alerts'}
                />
              ))
            )}
          </div>
        </section>
      )}

      {activeTab === 'compose' && (
        <NotificationMessagingSection
          senderRole="student"
          variant="student"
          section="compose"
          onSent={() => setSentCount((current) => current + 1)}
        />
      )}

      {activeTab === 'sent' && (
        <NotificationMessagingSection
          senderRole="student"
          variant="student"
          section="sent"
          onSentCountChange={setSentCount}
          onOpenConversation={(notificationId) => navigate(`/notifications/${notificationId}`)}
        />
      )}
    </div>
  );
}

export default NotificationsPage;
